import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  fetchParts, updatePart, deletePart, partsCsvUrl, fetchPartStatuses, fetchWatch,
} from "@/lib/api";
import PartFormSheet from "@/components/PartFormSheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import {
  Download, Search, ArrowUpRight, Trash2, ShoppingCart,
  Truck, CheckCircle2, AlertTriangle, X, Plus, Pencil,
} from "lucide-react";

const SHOPPING_STATUSES = ["Needed", "Backorder"];
const IN_TRANSIT_STATUSES = ["Ordered", "Shipped"];
const RECEIVED_STATUSES = ["Received"];

const fmtMoney = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

function statusTone(s) {
  switch (s) {
    case "Needed": return "bg-amber-500/10 text-amber-400 border-amber-500/20";
    case "Ordered": return "bg-sky-500/10 text-sky-400 border-sky-500/20";
    case "Shipped": return "bg-violet-500/10 text-violet-400 border-violet-500/20";
    case "Received": return "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
    case "Backorder": return "bg-rose-500/10 text-rose-400 border-rose-500/20";
    case "Cancelled": return "bg-neutral-500/10 text-[#3A2A10] border-neutral-500/20";
    default: return "bg-[#2B2214]/[0.14] text-[#3A2A10] border-[#8B6A1F]/45";
  }
}

export default function PartsShoppingList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();
  const watchIdFilter = searchParams.get("watch_id") || "";
  const statusFilter = searchParams.get("status") || "";
  const [filterWatchLabel, setFilterWatchLabel] = useState("");

  const [parts, setParts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qLive, setQLive] = useState("");
  const [q, setQ] = useState("");
  const [tab, setTab] = useState(statusFilter ? "all" : "shopping");
  const [confirmDel, setConfirmDel] = useState(null);
  // Shop-part form state (for adding supplies not tied to a specific watch)
  const [shopFormOpen, setShopFormOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);

  // resolve the watch label if filtered
  useEffect(() => {
    if (!watchIdFilter) {
      setFilterWatchLabel("");
      return;
    }
    fetchWatch(watchIdFilter)
      .then((w) => setFilterWatchLabel(`#${w.excel_id ?? "—"} ${w.brand || "Unbranded"}`))
      .catch(() => setFilterWatchLabel("watch"));
  }, [watchIdFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 1000, sort: "order_date", order: "desc" };
      if (q) params.q = q;
      if (watchIdFilter) params.watch_id = watchIdFilter;
      if (statusFilter) params.order_status = statusFilter;
      const r = await fetchParts(params);
      setParts(r.items);
    } finally {
      setLoading(false);
    }
  }, [q, watchIdFilter, statusFilter]);

  useEffect(() => { fetchPartStatuses().then(setStatuses).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => setQ(qLive), 250);
    return () => clearTimeout(t);
  }, [qLive]);

  // stats
  const stats = useMemo(() => {
    const byStatus = {};
    let outstandingSpend = 0;
    let shoppingEstimate = 0;
    for (const p of parts) {
      const s = p.order_status || "—";
      byStatus[s] = (byStatus[s] || 0) + 1;
      if (IN_TRANSIT_STATUSES.includes(s)) outstandingSpend += p.total_cost || 0;
      if (SHOPPING_STATUSES.includes(s)) shoppingEstimate += p.total_cost || 0;
    }
    return { byStatus, outstandingSpend, shoppingEstimate };
  }, [parts]);

  const filterByTab = (list) => {
    if (tab === "shopping") return list.filter((p) => SHOPPING_STATUSES.includes(p.order_status));
    if (tab === "transit") return list.filter((p) => IN_TRANSIT_STATUSES.includes(p.order_status));
    if (tab === "received") return list.filter((p) => RECEIVED_STATUSES.includes(p.order_status));
    return list;
  };

  const visible = filterByTab(parts);

  // group by supplier
  const grouped = useMemo(() => {
    const g = {};
    for (const p of visible) {
      const key = p.supplier || "— Unknown supplier —";
      (g[key] = g[key] || []).push(p);
    }
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [visible]);

  const quickUpdateStatus = async (part, newStatus) => {
    try {
      const payload = {
        watch_id: part.watch_id,
        part_name: part.part_name,
        part_type: part.part_type ?? null,
        supplier: part.supplier ?? null,
        order_status: newStatus,
        order_date: part.order_date ?? null,
        received_date:
          newStatus === "Received" && !part.received_date
            ? new Date().toISOString().slice(0, 10)
            : part.received_date ?? null,
        quantity: part.quantity ?? 1,
        cost_each: part.cost_each ?? null,
        tracking_number: part.tracking_number ?? null,
        notes: part.notes ?? null,
      };
      await updatePart(part.id, payload);
      toast.success(`Marked ${part.part_name} as ${newStatus}`);
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Status update failed"));
    }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await deletePart(confirmDel.id);
      toast.success(`Removed ${confirmDel.part_name}`);
      setConfirmDel(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const count = (s) => stats.byStatus[s] || 0;
  const shoppingCount = SHOPPING_STATUSES.reduce((a, s) => a + count(s), 0);
  const transitCount = IN_TRANSIT_STATUSES.reduce((a, s) => a + count(s), 0);
  const receivedCount = RECEIVED_STATUSES.reduce((a, s) => a + count(s), 0);

  return (
    <div className="px-6 md:px-10 py-8 pb-28 max-w-[1600px]">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-2">Workshop</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#0A0604] leading-none">Parts</h1>
          <div className="text-sm text-[#3A2A10] mt-3 font-mono-data">
            {loading ? "…" : `${parts.length.toLocaleString()} entries across all calibers`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => { setEditingPart(null); setShopFormOpen(true); }}
            className="inline-flex items-center gap-2 px-3 h-9 text-xs uppercase tracking-wider bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] rounded-sm"
            data-testid="add-shop-part-btn"
          >
            <Plus size={14} /> Add Shop Part
          </Button>
          <a
            href={partsCsvUrl()}
            className="inline-flex items-center gap-2 px-3 h-9 text-xs uppercase tracking-wider border border-[#8B6A1F]/45 hover:border-[#8B6A1F]/60 text-[#0A0604] hover:text-[#0A0604] transition-colors"
            data-testid="export-parts-csv"
          >
            <Download size={14} /> Export CSV
          </a>
        </div>
      </header>

      {/* Active deep-link filter banner */}
      {(watchIdFilter || statusFilter) && (
        <div className="flex items-center gap-3 mb-6 p-3 border border-[#D4AF37]/30 bg-[#D4AF37]/[0.04] rounded-sm" data-testid="active-filter-banner">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10]">Filtered:</span>
          {filterWatchLabel && (
            <span className="text-sm text-[#0A0604]">
              Watch <span className="font-mono-data text-[#3A2A10]">{filterWatchLabel}</span>
            </span>
          )}
          {statusFilter && (
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-sm ${statusTone(statusFilter)}`}>
              {statusFilter}
            </span>
          )}
          <button
            onClick={() => { setSearchParams({}); }}
            className="ml-auto inline-flex items-center gap-1 text-xs text-[#3A2A10] hover:text-[#3A2A10] transition-colors"
            data-testid="clear-deeplink-filter"
          >
            <X size={12} /> Clear filter
          </button>
        </div>
      )}

      {/* Stats strip */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={ShoppingCart} label="Shopping list" value={shoppingCount}
          sub={`Est. ${fmtMoney(stats.shoppingEstimate)}`}
          accent testid="stat-shopping"
        />
        <StatCard
          icon={Truck} label="In transit" value={transitCount}
          sub={`Outstanding ${fmtMoney(stats.outstandingSpend)}`}
          testid="stat-transit"
        />
        <StatCard
          icon={CheckCircle2} label="Received" value={receivedCount}
          sub="Logged as arrived"
          testid="stat-received"
        />
        <StatCard
          icon={AlertTriangle} label="Backorder" value={count("Backorder")}
          sub="Supplier waiting list"
          testid="stat-backorder"
        />
      </section>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3A2A10]" />
        <Input
          value={qLive}
          onChange={(e) => setQLive(e.target.value)}
          placeholder="Search part, supplier, tracking…"
          className="pl-9 h-10 bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm focus-visible:ring-[#D4AF37]/40"
          data-testid="parts-global-search"
        />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-[#F9F0D4] border border-[#8B6A1F]/35 rounded-sm p-0 h-auto mb-5">
          <TabTrigger value="shopping" label="Shopping List" count={shoppingCount} testid="tab-shopping" />
          <TabTrigger value="transit" label="In Transit" count={transitCount} testid="tab-transit" />
          <TabTrigger value="received" label="Received" count={receivedCount} testid="tab-received" />
          <TabTrigger value="all" label="All" count={parts.length} testid="tab-all" />
        </TabsList>

        <TabsContent value={tab} className="mt-0">
          {!loading && visible.length === 0 ? (
            <div className="border border-[#8B6A1F]/35 bg-[#F9F0D4] py-20 text-center" data-testid="parts-global-empty">
              <div className="font-serif-display text-2xl text-[#0A0604] mb-2">
                {tab === "shopping" && "Your shopping list is clear"}
                {tab === "transit" && "Nothing in transit"}
                {tab === "received" && "No parts received yet"}
                {tab === "all" && "No parts tracked yet"}
              </div>
              <div className="text-xs text-[#3A2A10] uppercase tracking-[0.2em]">
                Add parts from any movement detail page
              </div>
            </div>
          ) : (
            <div className="space-y-8" data-testid="parts-groups">
              {grouped.map(([supplier, items]) => (
                <SupplierBlock
                  key={supplier}
                  supplier={supplier}
                  items={items}
                  statuses={statuses}
                  onStatusChange={quickUpdateStatus}
                  onEdit={(p) => { setEditingPart(p); setShopFormOpen(true); }}
                  onDelete={setConfirmDel}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl">Remove part?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#3A2A10]">
              This will permanently delete <span className="text-[#0A0604]">{confirmDel?.part_name}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#8B6A1F]/45 rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700 text-white rounded-sm" data-testid="parts-global-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PartFormSheet
        open={shopFormOpen}
        onOpenChange={setShopFormOpen}
        part={editingPart}
        watchId={editingPart?.watch_id || null}
        onSaved={() => { setShopFormOpen(false); setEditingPart(null); load(); }}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div
      className="bg-[#F9F0D4] border border-[#8B6A1F]/35 p-5 flex flex-col gap-4 hover:border-[#D4AF37]/30 transition-colors"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">{label}</span>
        <Icon size={14} className={accent ? "text-[#3A2A10]" : "text-[#3A2A10]"} />
      </div>
      <div className={`font-mono-data text-3xl ${accent ? "text-[#3A2A10]" : "text-[#0A0604]"}`}>{value}</div>
      {sub && <div className="text-xs text-[#3A2A10] font-mono-data">{sub}</div>}
    </div>
  );
}

function TabTrigger({ value, label, count, testid }) {
  return (
    <TabsTrigger
      value={value}
      className="rounded-none border-r border-[#8B6A1F]/35 last:border-r-0 px-5 py-2.5 text-xs uppercase tracking-[0.2em] data-[state=active]:bg-[#2B2214]/[0.14] data-[state=active]:text-[#3A2A10] data-[state=active]:shadow-none"
      data-testid={testid}
    >
      {label}
      <span className="ml-2 font-mono-data text-[#3A2A10]">{count}</span>
    </TabsTrigger>
  );
}

function SupplierBlock({ supplier, items, statuses, onStatusChange, onEdit, onDelete }) {
  const totalOutstanding = items
    .filter((p) => ["Needed", "Backorder", "Ordered", "Shipped"].includes(p.order_status))
    .reduce((s, p) => s + (p.total_cost || 0), 0);

  return (
    <section className="border border-[#8B6A1F]/35 bg-[#F9F0D4]">
      <header className="flex items-center justify-between px-4 py-3 border-b border-[#8B6A1F]/35">
        <div className="flex items-center gap-3">
          <div className="font-serif-display text-xl text-[#0A0604]">{supplier}</div>
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10] font-mono-data">
            {items.length} {items.length === 1 ? "part" : "parts"}
          </span>
        </div>
        {totalOutstanding > 0 && (
          <div className="text-xs text-[#3A2A10]">
            Outstanding:{" "}
            <span className="font-mono-data text-[#3A2A10] ml-1">{fmtMoney(totalOutstanding)}</span>
          </div>
        )}
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#8B6A1F]/30">
              {["Part", "Type", "Watch", "Status", "Qty", "Cost/ea", "Total", "Ordered", "Tracking", "Actions"].map((h) => (
                <th key={h} className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.15em] text-[#3A2A10] font-normal whitespace-nowrap">{h === "Actions" ? "" : h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-[#8B6A1F]/30 hover:bg-[#2B2214]/[0.06] group" data-testid={`global-part-${p.id}`}>
                <td className="px-3 py-3 text-[#0A0604]">{p.part_name}</td>
                <td className="px-3 py-3 text-[#3A2A10]">{p.part_type || "—"}</td>
                <td className="px-3 py-3">
                  {p.watch_id ? (
                    <Link
                      to={`/inventory/${p.watch_id}`}
                      className="inline-flex items-center gap-1.5 text-[#0A0604] hover:text-[#3A2A10] transition-colors"
                      data-testid={`global-part-watch-${p.id}`}
                    >
                      <span className="font-mono-data text-[#3A2A10]">#{p.watch_excel_id ?? "—"}</span>
                      <span className="truncate max-w-[160px]">{p.watch_brand || "—"}</span>
                      <ArrowUpRight size={11} />
                    </Link>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-sm bg-[#D4AF37]/10 text-[#3A2A10] border-[#D4AF37]/30" data-testid={`global-part-shop-${p.id}`}>
                      <ShoppingCart size={10} /> Shop
                    </span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <Select value={p.order_status || "Needed"} onValueChange={(v) => onStatusChange(p, v)}>
                    <SelectTrigger
                      className={`h-7 text-[10px] uppercase tracking-wider px-2 py-1 border rounded-sm min-w-[110px] ${statusTone(p.order_status)}`}
                      data-testid={`global-status-${p.id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm">
                      {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-3 font-mono-data text-[#0A0604] text-right">{p.quantity ?? "—"}</td>
                <td className="px-3 py-3 font-mono-data text-[#0A0604] text-right">{fmtMoney(p.cost_each)}</td>
                <td className="px-3 py-3 font-mono-data text-[#3A2A10] text-right">{fmtMoney(p.total_cost)}</td>
                <td className="px-3 py-3 font-mono-data text-[#3A2A10] whitespace-nowrap">{p.order_date || "—"}</td>
                <td className="px-3 py-3 font-mono-data text-[#3A2A10] text-xs">{p.tracking_number || "—"}</td>
                <td className="px-3 py-3 text-right">
                  <div className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                    <button
                      onClick={() => onEdit(p)}
                      className="p-1.5 text-[#3A2A10] hover:text-[#0A0604]"
                      data-testid={`global-part-edit-${p.id}`}
                      aria-label="Edit"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => onDelete(p)}
                      className="p-1.5 text-[#3A2A10] hover:text-rose-400"
                      data-testid={`global-part-delete-${p.id}`}
                      aria-label="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
